<template>
    <div class="shipments-wigs-list">
        <div class="shipments-wigs-list__content">
            <div v-if="!hasManager" class="shipments-wigs-list__empty-message">
                <v-icon large color="grey">mdi-information-outline</v-icon>
                <p>{{ $t('pages.shipments.selectManager') }}</p>
            </div>
            <div v-else class="shipments-wigs-list__section">
                <div class="shipments-wigs-list__header">
                    <h3>{{ $t('pages.shipments.selectWigs') }}</h3>
                    <v-progress-circular
                        v-if="isLoading"
                        indeterminate
                        size="24"
                        color="primary"
                    ></v-progress-circular>
                </div>

                <v-alert
                    v-if="!isLoading && wigs.length === 0"
                    type="warning"
                    dense
                    class="shipments-wigs-list__alert"
                >
                    {{ $t('pages.shipments.noWigsAvailable') }}
                </v-alert>

                <div
                    v-else-if="
                        managersWithWigs.length > 0 || wigs.length > 0
                    "
                    class="shipments-wigs-list__table-container"
                >
                    <v-expansion-panels
                        v-if="managersWithWigs && managersWithWigs.length > 0"
                        v-model="activeManagerPanel"
                        class="shipments-wigs-list__panels"
                    >
                        <v-expansion-panel
                            v-for="manager in managersWithWigs"
                            :key="manager.id"
                            class="shipments-wigs-list__manager-group"
                        >
                            <v-expansion-panel-header
                                class="shipments-wigs-list__manager-header"
                            >
                                <div
                                    class="shipments-wigs-list__manager-header-content"
                                >
                                    <h4
                                        class="shipments-wigs-list__manager-name"
                                    >
                                        {{ manager.nickName }}
                                    </h4>
                                    <span
                                        class="shipments-wigs-list__manager-count"
                                    >
                                        ({{ manager.wigs?.length || 0 }})
                                    </span>
                                </div>
                            </v-expansion-panel-header>
                            <v-expansion-panel-content
                                class="shipments-wigs-list__manager-content"
                            >
                                <v-data-table
                                    :value="
                                        getSelectedWigsForManager(manager.id)
                                    "
                                    :headers="headers"
                                    :items="manager.wigs || []"
                                    :items-per-page="-1"
                                    :item-disabled="isWigDisabled"
                                    hide-default-footer
                                    dense
                                    show-select
                                    :loading="isLoading"
                                    class="shipments-wigs-list__table"
                                    @input="
                                        updateSelectedWigsForManager(
                                            manager.id,
                                            $event
                                        )
                                    "
                                >
                                    <template #[`item.barcode`]="{ item }">
                                        <div
                                            class="shipments-wigs-list__barcode-cell"
                                        >
                                            <span>{{ item.barcode }}</span>
                                            <v-icon
                                                v-if="item.is_repair"
                                                small
                                                color="error"
                                            >
                                                mdi-wrench
                                            </v-icon>
                                        </div>
                                    </template>
                                    <template #[`item.client`]="{ item }">
                                        {{ item.client || '-' }}
                                    </template>
                                    <template #[`item.price`]="{ item }">
                                        <span class="nowrap-cell">{{ formatWigPrice(item) }}</span>
                                    </template>
                                    <template #[`item.edit`]="{ item }">
                                        <v-btn
                                            icon
                                            small
                                            color="primary"
                                            @click.stop="openEditModal(item)"
                                        >
                                            <v-icon small>mdi-pencil</v-icon>
                                        </v-btn>
                                    </template>
                                </v-data-table>
                            </v-expansion-panel-content>
                        </v-expansion-panel>
                    </v-expansion-panels>
                    <v-data-table
                        v-else
                        :value="selectedWigs"
                        :headers="headers"
                        :items="wigs"
                        :items-per-page="-1"
                        :item-disabled="isWigDisabled"
                        hide-default-footer
                        dense
                        show-select
                        :loading="isLoading"
                        class="shipments-wigs-list__table"
                        @input="handleWigsSelection($event)"
                    >
                        <template #[`item.barcode`]="{ item }">
                            <div class="shipments-wigs-list__barcode-cell">
                                <span>{{ item.barcode }}</span>
                                <v-icon
                                    v-if="item.is_repair"
                                    small
                                    color="error"
                                >
                                    mdi-wrench
                                </v-icon>
                            </div>
                        </template>
                        <template #[`item.client`]="{ item }">
                            {{ item.client || '-' }}
                        </template>
                        <template #[`item.price`]="{ item }">
                            {{ formatWigPrice(item) }}
                        </template>
                        <template #[`item.edit`]="{ item }">
                            <v-btn
                                icon
                                small
                                color="primary"
                                @click.stop="openEditModal(item)"
                            >
                                <v-icon small>mdi-pencil</v-icon>
                            </v-btn>
                        </template>
                    </v-data-table>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import popUpTypes from '@/types/pop-up-types'
import { actionTypes } from '@/store/modules/modals'
import { mapActions } from 'vuex'
import {
    formatPriceWithCurrency,
    getCurrencySymbol,
} from '@/helpers/format-price-with-currency'

export default {
    name: 'ShipmentsWigsList',
    props: {
        wigs: {
            type: Array,
            default: () => [],
        },
        managersWithWigs: {
            type: Array,
            default: () => [],
        },
        selectedWigs: {
            type: Array,
            default: () => [],
        },
        isLoading: {
            type: Boolean,
            default: false,
        },
        hasManager: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['update:selectedWigs', 'refresh-wigs', 'currency-validation-error'],
    data() {
        return {
            activeManagerPanel: null,
        }
    },
    computed: {
        headers() {
            return [
                {
                    text: this.$t('pages.shipments.barcode'),
                    value: 'barcode',
                    sortable: false,
                },
                {
                    text: this.$t('pages.shipments.client'),
                    value: 'client',
                    sortable: false,
                },
                {
                    text: this.$t('pages.shipments.price'),
                    value: 'price',
                    sortable: false,
                },
                {
                    text: this.$t('pages.shipments.editWig'),
                    value: 'edit',
                    sortable: false,
                    align: 'center',
                    width: '120px',
                },
            ]
        },
        selectedCurrency() {
            if (!this.selectedWigs || this.selectedWigs.length === 0) {
                return null
            }
            const currencies = this.selectedWigs
                .map((wig) => wig.price_currency || 'ILS')
                .filter(
                    (currency, index, self) => self.indexOf(currency) === index
                )
            return currencies.length === 1 ? currencies[0] : null
        },
    },
    methods: {
        ...mapActions('modals', {
            openPopUp: actionTypes.openPopUp,
        }),
        getSelectedWigsForManager(managerId) {
            return this.selectedWigs.filter((wig) => {
                if (wig.managerId) {
                    return wig.managerId === managerId
                }

                const manager = this.managersWithWigs.find(
                    (m) => m.id === managerId
                )

                if (manager && manager.wigs) {
                    return manager.wigs.some((w) => w.id === wig.id)
                }

                return false
            })
        },
        isWigDisabled(wig) {
            if (!this.selectedCurrency) {
                return false
            }
            const wigCurrency = wig.price_currency || 'ILS'
            return wigCurrency !== this.selectedCurrency
        },
        formatWigPrice(wig) {
            const currency = wig.price_currency || 'ILS'
            if (wig.price != null && wig.price !== 0) {
                return formatPriceWithCurrency(wig.price, currency)
            }
            return getCurrencySymbol(currency)
        },
        handleWigsSelection(selectedWigs) {
            const validSelectedWigs = selectedWigs.filter(
                (wig) => !this.isWigDisabled(wig)
            )

            const disabledWigs = selectedWigs.filter((wig) =>
                this.isWigDisabled(wig)
            )

            if (disabledWigs.length > 0) {
                this.$emit('currency-validation-error')
            }

            this.$emit('update:selectedWigs', validSelectedWigs)
        },
        updateSelectedWigsForManager(managerId, selectedWigsForManager) {
            const manager = this.managersWithWigs.find(
                (m) => m.id === managerId
            )
            if (!manager) {
                return
            }

            const managerWigIds = (manager.wigs || []).map((w) => w.id)
            const isWigOfManager = (wig) =>
                wig.managerId
                    ? wig.managerId === managerId
                    : managerWigIds.includes(wig.id)

            const hasDisabledWigs = selectedWigsForManager.some((wig) =>
                this.isWigDisabled(wig)
            )
            if (hasDisabledWigs) {
                this.$emit('currency-validation-error')
            }

            const wigsForManager = selectedWigsForManager
                .filter((wig) => !this.isWigDisabled(wig))
                .map((wig) =>
                    wig.managerId
                        ? wig
                        : {
                              ...wig,
                              managerId,
                              managerNickName: manager.nickName,
                          }
                )

            const allSelectedWigs = this.replaceFirstGroup(
                this.selectedWigs,
                isWigOfManager,
                wigsForManager
            )

            this.$emit('update:selectedWigs', allSelectedWigs)
        },
        replaceFirstGroup(items, isInGroup, replacement) {
            const result = []
            let replaced = false
            for (const item of items) {
                if (!isInGroup(item)) {
                    result.push(item)
                    continue
                }
                if (!replaced) {
                    result.push(...replacement)
                    replaced = true
                }
            }
            if (!replaced) {
                result.push(...replacement)
            }
            return result
        },
        openEditModal(item) {
            this.openPopUp({
                name: popUpTypes.shipmentWigEditBarcode,
                payload: {
                    wigId: item.id,
                    wig: item,
                    onSuccess: () => {
                        this.$emit('refresh-wigs')
                    },
                },
            })
        },
    },
}
</script>

<style lang="scss" scoped>
.shipments-wigs-list {
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: white;
    border-radius: 8px;
    padding: 16px;

    &__content {
        height: 100%;
        overflow-y: auto;
    }

    &__empty-message {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 100%;
        color: #757575;
        text-align: center;
        padding: 32px;

        p {
            margin-top: 16px;
            font-size: 16px;
        }
    }

    &__section {
        height: 100%;
        display: flex;
        flex-direction: column;
    }

    &__header {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 16px;
    }

    &__table-container {
        flex: 1;
        overflow-y: scroll;
        min-height: 0;
        max-height: 50vh;
        padding-right: 4px;
        scrollbar-gutter: stable;

        @include tablet-large-up {
            max-height: none;
        }

        @include for-hebrew {
            padding-right: 0;
            padding-left: 4px;
        }
    }

    &__table {
    }

    &__barcode-cell {
        display: inline-flex;
        align-items: center;
        gap: 6px;
    }

    &__alert {
        margin-top: 16px;
    }

    &__panels {
        gap: 8px;
        display: flex;
        flex-direction: column;
    }

    &__manager-group {
        border: 1px solid #e0e0e0;
        border-radius: 4px !important;
        background: #fff;
        overflow: hidden;

        &::before {
            box-shadow: none;
        }

        &::after {
            display: none;
        }

        &.v-expansion-panel--active,
        &.v-expansion-panel--active + .v-expansion-panel {
            margin-top: 0;
        }
    }

    &__manager-header {
        background: #f5f5f5;
        min-height: 48px;
        padding: 8px 12px;
    }

    &__manager-header-content {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    &__manager-name {
        font-size: 16px;
        font-weight: 600;
        color: #333;
        margin: 0;
    }

    &__manager-count {
        font-size: 14px;
        color: #666;
    }

    &__manager-content {
        ::v-deep .v-expansion-panel-content__wrap {
            padding: 0;
        }
    }
}
</style>
